import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button,
  CircularProgress, Alert, InputAdornment, IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Lock } from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

export default function ActivateAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Enlace de activación inválido');
      setLoading(false);
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const data = await apiRequest(`/auth/validate-token?token=${encodeURIComponent(token)}&type=invite`);
        if (data.valid) {
          setTokenValid(true);
          setUserName(data.first_name || '');
          setUserEmail(data.email || '');
        } else {
          setError(data.error || 'Token inválido');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'No se pudo validar el enlace');
      } finally {
        setLoading(false);
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('La contraseña debe tener al menos una mayúscula');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('La contraseña debe tener al menos una minúscula');
      return;
    }
    if (!/[0-9]/.test(password)) {
      setError('La contraseña debe tener al menos un número');
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setSubmitting(true);
    try {
      await apiRequest('/auth/activate', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al activar la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F1F5F9' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.07)' }}>
          {/* Header */}
          <Box sx={{
            background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
            py: 5, px: 4, textAlign: 'center'
          }}>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, letterSpacing: '-0.5px' }}>
              Vincula
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', mt: 1, fontSize: 14 }}>
              Plataforma de acompañamiento
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ p: 5 }}>
            {success ? (
              <Box textAlign="center">
                <CheckCircle sx={{ fontSize: 64, color: '#10B981', mb: 2 }} />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  ¡Cuenta activada!
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Tu cuenta ha sido activada exitosamente. Ya podés iniciar sesión.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                    borderRadius: 3, px: 6, py: 1.5, fontWeight: 700,
                    textTransform: 'none', fontSize: 16
                  }}
                >
                  Iniciar sesión
                </Button>
              </Box>
            ) : !tokenValid ? (
              <Box textAlign="center">
                <Lock sx={{ fontSize: 64, color: '#94A3B8', mb: 2 }} />
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Enlace inválido
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  {error || 'Este enlace de activación no es válido o ya expiró. Contactá al administrador para que te reenvíe la invitación.'}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{ borderRadius: 3, textTransform: 'none' }}
                >
                  Ir al inicio de sesión
                </Button>
              </Box>
            ) : (
              <>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  ¡Hola {userName}!
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  Elegí una contraseña para activar tu cuenta:
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8', mb: 3 }}>
                  {userEmail}
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 0.5 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {password && (
                    <Box sx={{ mb: 2, pl: 0.5 }}>
                      {[
                        { ok: password.length >= 8, label: '8 caracteres mínimo' },
                        { ok: /[A-Z]/.test(password), label: 'Una mayúscula' },
                        { ok: /[a-z]/.test(password), label: 'Una minúscula' },
                        { ok: /[0-9]/.test(password), label: 'Un número' },
                      ].map((rule) => (
                        <Typography key={rule.label} variant="caption" sx={{ display: 'block', color: rule.ok ? '#10B981' : '#94A3B8', lineHeight: 1.8 }}>
                          {rule.ok ? '✓' : '○'} {rule.label}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    label="Confirmar contraseña"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    sx={{ mb: 3 }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={submitting || !password || !confirmPassword}
                    sx={{
                      background: 'linear-gradient(135deg, #2563EB, #7C3AED)',
                      borderRadius: 3, py: 1.5, fontWeight: 700,
                      textTransform: 'none', fontSize: 16,
                      '&:disabled': { opacity: 0.6 }
                    }}
                  >
                    {submitting ? <CircularProgress size={24} color="inherit" /> : 'Activar mi cuenta'}
                  </Button>
                </form>
              </>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
